/**
 * R2 outage detection for <video> elements.
 *
 * Backend context: when Cloudflare R2 has a sustained read-path
 * outage (we hit one on 2026-05-10), `streamReel.js` returns
 *
 *   503 + body { code: 'R2_OUTAGE', message: '…', retryAfterSeconds: 60 }
 *   Retry-After: 60
 *
 * instead of silently 404ing or hanging on the timeout. The HTML5
 * <video> element fires its `error` event in both cases, but the
 * MediaError object doesn't expose the underlying HTTP status —
 * we have to probe the URL ourselves to distinguish "transient
 * storage outage" from "real broken video / missing file".
 *
 * Usage:
 *   const v = videoRef.current;
 *   v.addEventListener('error', async () => {
 *     const outage = await detectVideoOutage(v.currentSrc || v.src);
 *     if (outage.isOutage) setOutageState(outage);
 *   });
 *
 * Or via the React hook below which wires up the listener for you.
 */

import { useEffect, useRef, useState } from 'react';

/**
 * Probe a video URL with a 1-byte Range request and read the
 * response status + body. Returns `{ isOutage: true, retryAfterSeconds,
 * message }` only if the backend explicitly signals R2_OUTAGE.
 * Any other failure (network error, 404, 403) returns `{ isOutage: false }`
 * so the caller falls back to the existing generic error UI.
 */
export async function detectVideoOutage(videoUrl) {
  if (!videoUrl) return { isOutage: false };
  try {
    // Range: bytes=0-0 means "give me the first byte only" — we never
    // want to pull the whole MP4 just to read a status code. Credentials
    // included because the stream routes are token- or session-gated.
    const resp = await fetch(videoUrl, {
      method: 'GET',
      headers: { Range: 'bytes=0-0' },
      credentials: 'include',
      cache: 'no-store',
    });
    if (resp.status !== 503) return { isOutage: false };

    // 503 — confirm it's the structured R2_OUTAGE response, not a
    // generic load-balancer 503 or upstream-Express crash.
    let body = null;
    try { body = await resp.clone().json(); } catch (_) { /* not JSON, fall through */ }
    if (!body || body.code !== 'R2_OUTAGE') return { isOutage: false };

    const headerSeconds = parseInt(resp.headers.get('Retry-After') || '', 10);
    const retryAfterSeconds = Number.isFinite(headerSeconds) && headerSeconds > 0
      ? headerSeconds
      : (Number.isFinite(body.retryAfterSeconds) ? body.retryAfterSeconds : 60);

    return {
      isOutage: true,
      retryAfterSeconds,
      message: body.message || 'Object storage is having a transient incident. Please try again in a few minutes.',
    };
  } catch (_) {
    // Network error — likely offline, not specifically an R2 outage.
    // Let the existing error UI handle it.
    return { isOutage: false };
  }
}

/**
 * React hook: pass a ref to a <video> element and the URL it's pulling.
 * Returns the current outage state ({ isOutage, retryAfterSeconds,
 * message } | null), a `retry()` function the user can call manually,
 * and `secondsUntilAutoRetry` which ticks down to 0 then auto-clears
 * the outage so the <video> can re-attempt by re-rendering.
 *
 * Intentionally minimal — callers render their own overlay UI on top
 * of the <video>. Keeps each surface (Share, Buyer Space, etc.) in
 * control of styling.
 */
export function useVideoOutageDetect(videoRef, videoUrl) {
  const [outage, setOutage] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  // Bounded auto-retry counter for GENERIC (non-outage) errors — covers
  // "trailer just published, CDN edge not warm yet" hands-free without
  // looping forever on a genuinely missing/revoked video.
  const genericAutoRetriesRef = useRef(0);

  // Listen for video errors + probe.
  useEffect(() => {
    const v = videoRef && videoRef.current;
    if (!v || !videoUrl) return undefined;
    let alive = true;

    async function onError() {
      const result = await detectVideoOutage(videoUrl);
      if (!alive) return;
      if (result.isOutage) {
        setOutage(result);
        setSecondsLeft(result.retryAfterSeconds);
      } else {
        // Generic failure (404 / 403 / expired URL / network / decode).
        // Pre-fix (2026-05-29 audit P0): this branch did NOTHING, so the
        // prospect saw a silent black player — the worst possible look on
        // a seller's share page. Reuse the same overlay with honest copy
        // and a short auto-retry; after 2 auto-attempts the overlay stays
        // with a manual Retry button.
        setOutage({
          isOutage: false,
          generic: true,
          message: "This video isn't loading right now. It may still be publishing — it usually sorts itself out in a few seconds.",
          retryAfterSeconds: 8,
        });
        setSecondsLeft(8);
      }
    }
    v.addEventListener('error', onError);
    // Reset the outage banner if the video starts playing — covers
    // the case where R2 recovers mid-session and the user manually
    // retries faster than the auto-timer.
    function onPlaying() {
      if (alive) {
        setOutage(null);
        setSecondsLeft(0);
        genericAutoRetriesRef.current = 0;
      }
    }
    v.addEventListener('playing', onPlaying);
    return () => {
      alive = false;
      v.removeEventListener('error', onError);
      v.removeEventListener('playing', onPlaying);
    };
  }, [videoRef, videoUrl]);

  // Auto-retry for generic errors when the countdown reaches zero (max 2
  // automatic attempts; the R2_OUTAGE path keeps its original semantics —
  // consumers decide when to retry outages).
  useEffect(() => {
    if (!outage || !outage.generic) return;
    if (secondsLeft > 0) return;
    if (genericAutoRetriesRef.current >= 2) return;
    genericAutoRetriesRef.current += 1;
    retry();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outage, secondsLeft]);

  // Countdown timer for the auto-retry pill. Clears the outage when
  // it hits zero, which prompts the <video> to retry via the manual
  // .load() call in retry() (called from the visible Retry button or
  // by a useEffect on the consumer side).
  useEffect(() => {
    if (!outage || secondsLeft <= 0) return undefined;
    const id = setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [outage, secondsLeft]);

  function retry() {
    setOutage(null);
    setSecondsLeft(0);
    const v = videoRef && videoRef.current;
    if (v && typeof v.load === 'function') {
      try { v.load(); } catch (_) { /* noop */ }
    }
  }

  return { outage, secondsLeft, retry };
}
